import { INFINITE_SCROLL_ROOT_MARGIN } from '../../shared/config/constants'
import {
	infiniteListEndTemplate,
	infiniteListSentinelTemplate,
} from '../components/infiniteListTemplates'

interface InfiniteListPage<T> {
	items: T[]
	// Cursor-based feeds can return short pages while still having more results.
	hasMore?: boolean
}

interface InfiniteListConfig<T> {
	containerEl: () => HTMLElement | null
	fetchFunction: (
		offset: number,
		limit: number,
	) => Promise<null | InfiniteListPage<T>>
	onFetchingChange?: (isFetching: boolean, offset: number) => void
	onError?: (error: unknown) => void
	limit: number
	renderItem: (item: T, index: number) => HTMLElement | HTMLElement[]
	initialItems?: T[]
	initialHasMore?: boolean
}

class InfiniteListController<T> {
	private readonly sentinelEl: HTMLElement | null
	private readonly endEl: HTMLElement | null
	private buffer: T[]
	private isLoading = false
	private isDestroyed = false
	private hasMore = true
	private bufferIndex = 0
	private generation = 0
	private failed = false
	private sourceHasMore: boolean | undefined
	private paused = true
	private readonly observer: IntersectionObserver
	private readonly config: InfiniteListConfig<T>

	constructor(config: InfiniteListConfig<T>) {
		this.config = config
		this.buffer = config.initialItems ? [...config.initialItems] : []
		this.sourceHasMore = config.initialHasMore
		this.sentinelEl = this.createElement(infiniteListSentinelTemplate())
		this.endEl = this.createElement(infiniteListEndTemplate())

		this.observer = new IntersectionObserver(
			(entries) => {
				if (this.isDestroyed || this.paused) return
				if (
					entries.some((entry) => entry.isIntersecting) &&
					this.isActive()
				) {
					void this.load()
				}
			},
			{ rootMargin: `0px 0px ${INFINITE_SCROLL_ROOT_MARGIN} 0px` },
		)
	}
	async load(): Promise<void> {
		if (this.isDestroyed) return
		if (this.isLoading || !this.hasMore || this.failed) return
		if (!this.sentinelEl) return

		const container = this.config.containerEl()
		if (!container) return

		// Attach sentinel once next to the active list container.
		if (
			!this.sentinelEl.isConnected ||
			this.sentinelEl.previousElementSibling !== container
		) {
			this.observer.disconnect()
			container.insertAdjacentElement('afterend', this.sentinelEl)
			if (this.endEl)
				this.sentinelEl.insertAdjacentElement('afterend', this.endEl)
			if (!this.paused) this.observer.observe(this.sentinelEl)
		}

		this.isLoading = true
		const generation = this.generation
		let notifiedFetching = false

		try {
			const haveBufferedItems = this.bufferIndex < this.buffer.length
			if (!haveBufferedItems && this.sourceHasMore !== false) {
				const fetchOffset = this.bufferIndex
				this.config.onFetchingChange?.(true, fetchOffset)
				notifiedFetching = true
				const fetched = await this.config.fetchFunction(
					this.bufferIndex,
					this.config.limit,
				)
				if (this.isDestroyed || generation !== this.generation) return
				// An invalidated request is not an empty page or the end of the list.
				if (!fetched) return
				this.sourceHasMore = fetched.hasMore
				this.buffer.push(...fetched.items)
			}

			const itemsToRender = this.buffer.slice(
				this.bufferIndex,
				this.bufferIndex + this.config.limit,
			)
			this.bufferIndex += itemsToRender.length

			itemsToRender.forEach((item, idx) => {
				const rendered = this.config.renderItem(
					item,
					this.bufferIndex - itemsToRender.length + idx,
				)
				const elements = Array.isArray(rendered) ? rendered : [rendered]
				elements.forEach((el) => {
					container.insertAdjacentElement('beforeend', el)
				})
			})

			if (this.bufferIndex >= this.buffer.length) {
				this.hasMore =
					this.sourceHasMore ??
					itemsToRender.length === this.config.limit
				this.setEndVisible(!this.hasMore && this.bufferIndex > 0)
			}
		} catch (err) {
			if (this.isDestroyed || generation !== this.generation) return
			console.error('Infinite scroll load failed', err)
			this.failed = true
			this.config.onError?.(err)
		} finally {
			if (!this.isDestroyed && generation === this.generation) {
				this.isLoading = false
				if (notifiedFetching)
					this.config.onFetchingChange?.(false, this.bufferIndex)
				// Recheck visibility after each page, even if the sentinel never left
				// the viewport (large screens or short, filtered pages).
				this.observer.disconnect()
				if (!this.paused && this.hasMore && !this.failed)
					this.observer.observe(this.sentinelEl)
			}
		}
	}

	reset(options?: {
		initialItems?: T[]
		hasMore?: boolean
		fetchFunction?: (
			offset: number,
			limit: number,
		) => Promise<null | InfiniteListPage<T>>
	}): void {
		if (this.isDestroyed) return
		this.generation++
		this.observer.disconnect()
		if (options?.fetchFunction) {
			this.config.fetchFunction = options.fetchFunction
		}
		this.buffer = options?.initialItems ? [...options.initialItems] : []
		this.bufferIndex = 0
		this.hasMore = true
		this.sourceHasMore = options?.hasMore
		this.failed = false
		this.isLoading = false
		this.setEndVisible(false)
		this.config.onFetchingChange?.(false, 0)
	}

	destroy(): void {
		if (this.isDestroyed) return
		this.isDestroyed = true
		this.observer.disconnect()
		this.sentinelEl?.remove()
		this.endEl?.remove()
		this.config.onFetchingChange?.(false, this.bufferIndex)
	}

	async start(): Promise<void> {
		const generation = this.generation
		await this.load()
		while (
			!this.isDestroyed &&
			!this.isLoading &&
			!this.failed &&
			generation === this.generation &&
			this.bufferIndex < this.buffer.length
		)
			await this.load()
	}

	pause(): void {
		this.paused = true
		this.observer.disconnect()
	}

	cancelPending(): void {
		this.generation++
		this.isLoading = false
	}

	resume(): void {
		if (this.isDestroyed) return
		this.paused = false
		if (this.sentinelEl && this.hasMore && !this.failed)
			this.observer.observe(this.sentinelEl)
	}

	get count(): number {
		return this.bufferIndex
	}

	retry(): void {
		this.failed = false
		void this.start()
	}

	private isActive(): boolean {
		const container = this.config.containerEl()
		return !!container?.isConnected && !container.closest('[hidden]')
	}
	private setEndVisible(isVisible: boolean): void {
		if (this.endEl) this.endEl.hidden = !isVisible
	}
	private createElement(html: string): HTMLElement | null {
		const fragment = document
			.createRange()
			.createContextualFragment(html.trim())
		const element = fragment.firstElementChild
		return element instanceof HTMLElement ? element : null
	}
}

export function createInfiniteList<T>(config: InfiniteListConfig<T>) {
	const controller = new InfiniteListController(config)
	return {
		start: () => controller.start(),
		pause: () => controller.pause(),
		cancelPending: () => controller.cancelPending(),
		resume: () => controller.resume(),
		get count() {
			return controller.count
		},
		reset: (options?: {
			initialItems?: T[]
			hasMore?: boolean
			fetchFunction?: (
				offset: number,
				limit: number,
			) => Promise<null | InfiniteListPage<T>>
		}) => controller.reset(options),
		destroy: () => controller.destroy(),
		retry: () => controller.retry(),
	}
}
